/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useMemo } from 'react';
import cx from 'classnames';
import classNames from 'classnames';
import SimpleModal from '@broadleaf/admin-components/dist/common/components/SimpleModal';
import {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import { IFulfillmentSellerPaymentSummary } from '../../utils/RmFulfillmentUtils';
import {
  useFormatDate,
  useFormatNumber
} from '@broadleaf/admin-components/dist/common';
import { UserAccess } from '@broadleaf/admin-components/dist/authentication';
import { AddSellerPaymentForm } from '@src/oms/FulfillmentView/ManageSellerPaymentsModal/AddSellerPaymentForm';
import { IFulfillmentViewLocalState } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';
import { buildHelpText } from '@broadleaf/admin-components/dist/common/components/HelpText';

export interface ManageSellerPaymentsModalProps {
  actionDefinition: IMetadataAction;
  metadata: IMetadata;
  state: IFulfillmentViewLocalState;
  sellerPaymentSummary: IFulfillmentSellerPaymentSummary;
  onClose: () => void;
  refresh: () => Promise<void>;
  title: string;
  allAllowedStatuses?: string[];
}

export const ManageSellerPaymentsModal: React.FC<ManageSellerPaymentsModalProps> = ({
  metadata,
  actionDefinition,
  state,
  sellerPaymentSummary,
  onClose,
  refresh,
  title,
  allAllowedStatuses
}) => {
  return (
    <SimpleModal
      /*// @ts-ignore */
      closeOnClickOutside={false}
      isOpen
      onClose={onClose}
      size="lg"
      title={title}
    >
      <SellerPaymentsSection
        refresh={refresh}
        state={state}
        sellerPaymentSummary={sellerPaymentSummary}
        actionDefinition={actionDefinition}
        metadata={metadata}
        allAllowedStatuses={allAllowedStatuses}
      />
    </SimpleModal>
  );
};

type SellerPaymentsSectionProps = {
  actionDefinition: IMetadataAction;
  metadata: IMetadata;
  refresh: () => Promise<void>;
  state: IFulfillmentViewLocalState;
  sellerPaymentSummary: IFulfillmentSellerPaymentSummary;
  allAllowedStatuses?: string[];
};
const SellerPaymentsSection: React.FC<SellerPaymentsSectionProps> = ({
  state,
  sellerPaymentSummary,
  refresh,
  actionDefinition,
  metadata,
  allAllowedStatuses
}) => {
  const { data: fulfillment } = state;
  const checkAccess = UserAccess.useCheckAccess();
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();
  const hasMultipleSellers = !fulfillment.vendorRef;
  const hasUpdateAccess =
    checkAccess('ORDER_SELLER_PAYMENT', 'UPDATE') &&
    allAllowedStatuses.includes(state.data.status) &&
    !hasMultipleSellers;
  const [isAddPaymentOpen, setIsAddPaymentOpen] = React.useState(
    sellerPaymentSummary.sellerPayments.length === 0 && hasUpdateAccess
  );
  const fulfillmentFees = useMemo(() => {
    return (
      fulfillment.merchandiseTotal.amount -
      fulfillment.attributes.sellerNetTotal
    );
  }, [fulfillment]);

  const sellerNetBalance = useMemo(() => {
    return (
      fulfillment.attributes.sellerNetTotal - sellerPaymentSummary.totalAmount
    );
  }, [fulfillment, sellerPaymentSummary]);

  return (
    <>
      <div className="tw-flex-1 tw-text-lg">Seller Payment Summary</div>
      <div className="tw-flex tw-flex-1 tw-flex-row">
        <table className="tw-w-full tw-divide-y tw-divide-gray-200">
          <thead>
            <tr>
              <th className="tw-px-4 tw-py-2 tw-text-right">
                Merchandise Total
              </th>
              <th className="tw-px-4 tw-py-2 tw-text-right">
                Commission &amp; Fees
              </th>
              <th className="tw-px-4 tw-py-2 tw-text-right">
                Payments Applied
              </th>
              <th className="tw-px-4 tw-py-2 tw-text-right">
                Seller Net Balance
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className={cx('tw-bg-gray-100 hover:tw-bg-gray-200')}>
              <td className="tw-px-4 tw-py-1 tw-text-right">
                {formatNumber(fulfillment.merchandiseTotal.amount, {
                  style: 'currency',
                  currency: fulfillment.grandTotal.currency
                })}
              </td>
              <td className="tw-px-4 tw-py-1 tw-text-right">
                {formatNumber(fulfillmentFees, {
                  style: 'currency',
                  currency: fulfillment.grandTotal.currency
                })}
              </td>
              <td className="tw-px-4 tw-py-1 tw-text-right">
                {formatNumber(sellerPaymentSummary.totalAmount, {
                  style: 'currency',
                  currency: fulfillment.grandTotal.currency
                })}
              </td>
              <td className="tw-px-4 tw-py-1 tw-text-right">
                {formatNumber(sellerNetBalance, {
                  style: 'currency',
                  currency: fulfillment.grandTotal.currency
                })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="tw-mt-3 tw-flex-1 tw-text-lg">
        Seller Payments ({sellerPaymentSummary.sellerPayments.length}){' '}
        {hasUpdateAccess && sellerNetBalance > 0 && (
          <a
            className={classNames(
              'tw-ml-2 tw-cursor-pointer tw-text-sm tw-text-primary-500 hover:tw-text-primary-600',
              {
                'tw-text-primary-500': isAddPaymentOpen
              }
            )}
            onClick={() => setIsAddPaymentOpen(!isAddPaymentOpen)}
          >
            {isAddPaymentOpen ? 'Hide Payment Form' : 'Add Payment'}
          </a>
        )}
      </div>
      {sellerPaymentSummary.sellerPayments.length > 0 && (
        <div className="tw-flex tw-flex-1 tw-flex-row">
          <table className="tw-w-full tw-divide-y tw-divide-gray-200">
            <thead>
              <tr>
                <th className="tw-px-4 tw-py-2">Payment Date</th>
                <th className="tw-px-4 tw-py-2">Payment Description</th>
                <th className="tw-px-4 tw-py-2 tw-text-right">
                  Payment Amount
                </th>
                <th className="tw-px-4 tw-py-2"></th>
              </tr>
            </thead>
            <tbody>
              {sellerPaymentSummary.sellerPayments.map(payment => (
                <tr
                  key={payment.id}
                  className={cx('tw-bg-gray-100 hover:tw-bg-gray-200')}
                >
                  <td className="tw-px-4 tw-py-1">
                    {formatDate(payment.paymentDate, {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric'
                    })}
                  </td>
                  <td className="tw-px-4 tw-py-1">
                    {payment.reasonDescription}{' '}
                    <span className="tw-text-xs tw-text-gray-700">
                      {payment.paymentSourceType?.includes('MANUAL')
                        ? ' (Manual)'
                        : payment.paymentSourceType?.includes('AUTOMATIC')
                        ? ' (Auto-applied)'
                        : ''}
                    </span>
                    {payment.paymentSourceType?.includes('MANUAL') &&
                      buildHelpText(
                        'Triggered by: ' + payment?.paymentSourceId
                      )}
                  </td>
                  <td className="tw-px-4 tw-py-1 tw-text-right">
                    {formatNumber(payment.amount, {
                      style: 'currency',
                      currency: fulfillment.grandTotal.currency
                    })}
                  </td>
                  <td className="tw-px-4 tw-py-1 tw-text-right"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasMultipleSellers && sellerNetBalance > 0 && (
        <div className="tw-mt-3 tw-flex-1 tw-text-right tw-text-sm tw-text-gray-700">
          This fulfillment is split across multiple sellers. Payments cannot be
          applied manually.
        </div>
      )}
      {isAddPaymentOpen && (
        <AddSellerPaymentForm
          actionDefinition={actionDefinition}
          metadata={metadata}
          state={state}
          fulfillment={fulfillment}
          sellerPaymentSummary={sellerPaymentSummary}
          sellerNetBalance={sellerNetBalance}
          refresh={async () => {
            await refresh();
            setIsAddPaymentOpen(false);
          }}
        />
      )}
    </>
  );
};

export default ManageSellerPaymentsModal;
